/**
 * Language Detection and Auto-Switch Utility
 *
 * This script handles:
 * - Browser language detection
 * - Automatic language switching for returning users
 * - Language suggestion modal management
 * - localStorage preference management
 */

class LanguageDetector {
  constructor() {
    this.STORAGE_KEY = 'language_preference';
    this.MODAL_STORAGE_KEY = 'language_suggestion_dismissed';
    this.config = window.LanguageDetectionConfig || {};
    this.currentLanguage = this.config.currentLanguage || document.documentElement.lang || 'en';
    this.availableLanguages = this.config.availableLanguages || this.getAvailableLanguages();
    this.browserLanguage = this.detectBrowserLanguage();

    this.init();
  }

  /**
   * Get available languages from the page
   */
  getAvailableLanguages() {
    const languages = [];
    const languageLinks = document.querySelectorAll('link[hreflang]');

    languageLinks.forEach(link => {
      const lang = link.getAttribute('hreflang');
      if (lang && lang !== 'x-default') {
        languages.push(lang);
      }
    });

    if (languages.length === 0 && window.Shopify && window.Shopify.locale) {
      languages.push(window.Shopify.locale);
    }

    return languages;
  }

  /**
   * Detect user's preferred language from browser
   */
  detectBrowserLanguage() {
    const languages = navigator.languages || [navigator.language];

    for (const lang of languages) {
      const langCode = lang.split('-')[0].toLowerCase();
      const fullLangCode = lang.toLowerCase();

      // Check custom mapping first
      if (this.config.customLanguageMapping && Array.isArray(this.config.customLanguageMapping)) {
        for (const mapping of this.config.customLanguageMapping) {
          const [browserCode, shopifyCode] = mapping.split(':');
          if (browserCode === langCode && this.availableLanguages.includes(shopifyCode)) {
            return shopifyCode;
          }
        }
      }

      // Direct match (case insensitive)
      const directMatch = this.availableLanguages.find(available =>
        available.toLowerCase() === fullLangCode
      );
      if (directMatch) {
        return directMatch;
      }

      // Partial match on base code (e.g., 'en' matches 'en-US')
      const matchingLang = this.availableLanguages.find(available =>
        available.toLowerCase().startsWith(langCode)
      );

      if (matchingLang) {
        return matchingLang;
      }
    }

    return null;
  }

  /**
   * Get user's saved language preference
   */
  getSavedPreference() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save user's language preference
   */
  savePreference(language, source = 'manual') {
    try {
      const preference = {
        language: language,
        source: source, // 'manual', 'auto', 'suggestion'
        timestamp: Date.now(),
        userAgent: navigator.userAgent
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(preference));
    } catch (e) {
      console.warn('Error saving language preference:', e);
    }
  }

  /**
   * Check if user has dismissed language suggestion
   */
  hasDismissedSuggestion() {
    if (typeof window.hasUserDismissedLanguageSuggestion === 'function') {
      return window.hasUserDismissedLanguageSuggestion(this.browserLanguage);
    }
    return false;
  }

  /**
   * Switch to a different language using Shopify's localization system
   */
  switchLanguage(languageCode, primaryLocale = null) {
    if (!this.availableLanguages.includes(languageCode)) {
      return false;
    }

    this.savePreference(languageCode, 'auto');
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;

    let newUrl = '';
    const pathParts = currentPath.split('/').filter(p => p);

    const firstPart = pathParts[0];
    const isLanguageInPath = this.availableLanguages.some(lang =>
      lang.toLowerCase() === firstPart?.toLowerCase()
    );

    if (primaryLocale && languageCode === primaryLocale) {
      if (isLanguageInPath) {
        pathParts.shift();
      }
      newUrl = '/' + pathParts.join('/');
    } else {
      if (isLanguageInPath) {
        pathParts[0] = languageCode;
        newUrl = '/' + pathParts.join('/');
      } else {
        newUrl = '/' + languageCode + currentPath;
      }
    }

    newUrl += currentSearch + currentHash;

    window.location.href = newUrl;

    return true;
  }

  /**
   * Check if user should be auto-switched
   */
  shouldAutoSwitch() {
    const savedPreference = this.getSavedPreference();

    if (savedPreference &&
        savedPreference.language !== this.currentLanguage &&
        this.availableLanguages.includes(savedPreference.language)) {

      const expiryDays = this.config.preferenceExpiryDays || 30;
      const expiryTime = Date.now() - (expiryDays * 24 * 60 * 60 * 1000);
      if (savedPreference.timestamp > expiryTime) {
        return savedPreference.language;
      }
    }

    return null;
  }

  /**
   * Show language suggestion modal
   */
  showSuggestionModal() {
    if (!this.browserLanguage || this.hasDismissedSuggestion()) {
      return;
    }

    // Update modal with detected language
    if (typeof window.updateLanguageSuggestion === 'function') {
      const updated = window.updateLanguageSuggestion(this.browserLanguage);
      if (!updated) {
        return;
      }
    } else {
      return;
    }

    // Show the modal
    if (typeof window.showLanguageSuggestionModal === 'function') {
      window.showLanguageSuggestionModal();
      window.languageDetectorShown = true;
    }
  }

  /**
   * Remove primary locale from URL if present
   * Shopify omits the market primary locale from the path, so strip it when present
   */
  removePrimaryLocaleFromUrl() {
    const primaryLocale = (this.config.primaryLocale || '').toLowerCase();

    if (!primaryLocale || this.currentLanguage.toLowerCase() !== primaryLocale) {
      return false;
    }

    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;
    const pathParts = currentPath.split('/').filter(p => p);

    if (pathParts.length === 0) {
      return false;
    }

    const firstPart = pathParts[0].toLowerCase();

    if (firstPart === primaryLocale) {
      pathParts.shift();
      const newPath = '/' + pathParts.join('/');
      const newUrl = newPath + currentSearch + currentHash;

      if (newUrl !== window.location.pathname + currentSearch + currentHash) {
        window.location.replace(newUrl);
        return true;
      }
    }

    return false;
  }

  /**
   * Initialize the language detector
   */
  init() {
    // Strip primary locale from URL if present (Shopify omits it from the path)
    if (this.removePrimaryLocaleFromUrl()) {
      return;
    }

    // Check for auto-switch first (if enabled)
    if (this.config.enableAutoSwitch !== false) {
      const autoSwitchLanguage = this.shouldAutoSwitch();
      if (autoSwitchLanguage) {
        const delay = this.config.autoSwitchDelay || 500;
        setTimeout(() => {
          this.switchLanguage(autoSwitchLanguage, this.config.primaryLocale);
        }, delay);
        return;
      }
    }

    // If no auto-switch, check if we should show suggestion modal (if enabled)
    if (this.config.enableModal !== false &&
        this.browserLanguage &&
        this.browserLanguage !== this.currentLanguage &&
        this.availableLanguages.includes(this.browserLanguage)) {

      const delay = this.config.modalDelay || 1000;
      setTimeout(() => {
        this.showSuggestionModal();
      }, delay);
    }

    this.setupEventListeners();
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Listen for language form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.id === 'language-suggestion-form') {
        const localeInput = form.querySelector('input[name="locale_code"]');
        if (localeInput) {
          this.savePreference(localeInput.value, 'suggestion');
        }
      }
    });

    // Listen for manual language changes from various selectors
    document.addEventListener('change', (e) => {
      const target = e.target;

      // Check for locale/country code inputs
      if (target.name === 'locale_code' || target.name === 'country_code') {
        const form = target.closest('form');
        if (form && form.action.includes('localization')) {
          const localeInput = form.querySelector('input[name="locale_code"]');
          if (localeInput) {
            this.savePreference(localeInput.value, 'manual');
          }
        }
      }

      // Check for data attributes that might indicate language selection
      if (target.dataset.locale || target.dataset.language) {
        const langCode = (target.dataset.locale || target.dataset.language).toLowerCase();
        if (this.availableLanguages.includes(langCode)) {
          this.savePreference(langCode, 'manual');
        }
      }
    });

    // Listen for clicks on language links/buttons
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a, button');
      if (target) {
        // Check for localization selector buttons (the main language selector buttons)
        if (target.name === 'locale_code' && target.type === 'submit') {
          const langCode = target.value.toLowerCase();
          if (this.availableLanguages.includes(langCode)) {
            this.savePreference(langCode, 'manual');
          }
        }

        // Check for popover value options (language options in dropdown)
        if (target.classList.contains('popover__value-option') && target.name === 'locale_code') {
          const langCode = target.value.toLowerCase();
          if (this.availableLanguages.includes(langCode)) {
            this.savePreference(langCode, 'manual');
          }
        }

        // Check href for language codes
        if (target.href) {
          const url = new URL(target.href);
          const pathParts = url.pathname.split('/').filter(p => p);
          if (pathParts.length > 0) {
            const firstPart = pathParts[0].toLowerCase();
            if (this.availableLanguages.includes(firstPart)) {
              this.savePreference(firstPart, 'manual');
            }
          }
        }

        // Check data attributes
        if (target.dataset.locale || target.dataset.language) {
          const langCode = (target.dataset.locale || target.dataset.language).toLowerCase();
          if (this.availableLanguages.includes(langCode)) {
            this.savePreference(langCode, 'manual');
          }
        }
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LanguageDetector();
  });
} else {
  new LanguageDetector();
}

window.LanguageDetector = LanguageDetector;
